import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, type IntlShape, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Card, CardBody, Col, Row } from 'reactstrap';
import { useConfirmApi } from 'utils/api/ApiHooks';
import { useAuthProvider } from 'utils/api/useAuthProvider';
import {
  ApiCampaignReport,
  formatCampaignReportDescriptor,
} from 'views/Administration/CampaignReports/EngagementSurvey/EngagementSurvey';
import TakeawaysDashboard from 'views/Widgets/Dashboards/TakeawaysDashboard';
import ObjectsDropdown from 'views/Widgets/Dropdowns/ObjectsDropdown';
import { getDescriptionForFieldName } from 'views/Widgets/People/Table/commons';
import * as consts from '../../consts/consts';
import { Campaign, Organization, Person, ReduxState } from '../../types';
import ConfirmAPI from '../../utils/api/ConfirmAPI';
import { CAMPAIGN_STATUSES } from '../../utils/models/Campaign';
import { asInt } from '../../utils/util/util';
import Page from '../Layout/Pages/Page';
import PerformanceCycleDropdown from '../Widgets/Dropdowns/PerformanceCycleDropdown';
import EmptyState from '../Widgets/EmptyState';
import Loading from '../Widgets/Loading';

interface CampaignReportItem {
  id: number;
  name: string;
  report: ApiCampaignReport;
}

function buildQueryString(campaignId?: number) {
  const queryParameters: Record<string, string> = {
    ...Object.fromEntries(new URLSearchParams(location.search)),
    ...(campaignId && campaignId > 0
      ? { campaignId: campaignId.toString() }
      : {}),
  };
  const querystring = new URLSearchParams(queryParameters).toString();
  return querystring ? `?${querystring}` : '';
}

type EngagementHubPageProps = {
  currentOrganization: Organization;
  currentProxyPerson?: Person;
};

const EngagementHubPage: React.FC<EngagementHubPageProps> = (props) => {
  const { formatMessage } = useIntl();
  const [campaigns, setCampaigns] = useState<Campaign[] | null>(null);
  const [pageStatusIndicator, setPageStatusIndicator] = useState(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const history = useHistory();
  const location = useLocation();
  const { user } = useAuthProvider();
  const campaignId = asInt(
    new URLSearchParams(location.search).get('campaignId')
  );
  const [selectedReportItem, setSelectedReportItem] = useState<
    number | undefined
  >(undefined);

  const formatCampaignReportItem = useCallback(
    (reportItem: CampaignReportItem) => {
      const reportInfo = formatCampaignReportDescriptor(
        reportItem.report,
        formatMessage
      );

      const nameParts = [reportInfo.title];
      if (reportItem.report.config.slice_type === 'DEMOGRAPHIC') {
        const fieldName = getDescriptionForFieldName(
          reportItem.report.config.slice_id!,
          formatMessage
        );
        if (fieldName) {
          nameParts.push(fieldName);
        }
        nameParts.push(reportItem.name);
      } else if (reportItem.report.config.slice_type === 'LEADERS') {
        nameParts.push(reportItem.name);
      } else if (reportItem.report.config.slice_type === 'CUSTOM') {
        nameParts.push(reportItem.name);
      }

      return {
        id: reportItem.id == null ? undefined : reportItem.id, // make sure we return undefined for null
        name: nameParts.join(' - '),
      };
    },
    [formatMessage]
  );

  const { data: reportItemsData, status: reportItemsStatus } = useConfirmApi<
    CampaignReportItem[]
  >({
    url: `/campaigns/${campaignId || campaigns?.[0]?.id}/report-items`,
    method: 'GET',
    disabled: !campaignId && !campaigns?.length,
    clearOnChange: true,
    clearOnError: true,
  });

  const allReportItems = useMemo(
    () => [...(reportItemsData?.map(formatCampaignReportItem) ?? [])],
    [reportItemsData, formatCampaignReportItem]
  );

  useEffect(() => {
    setLoading(true);
    // will fix when ConfirmAPI.js is converted
    ConfirmAPI.getUrlWithCache(
      '/campaigns',
      'campaigns',
      user?.sub,
      props.currentProxyPerson,
      {
        status: CAMPAIGN_STATUSES.ACTIVE,
        organization: props.currentOrganization?.id,
      },
      (data) => {
        if (data?.results) {
          setCampaigns(
            data.results.filter((x: Campaign) => !!x?.properties?.has_survey)
          );
        }
        setLoading(false);
      },
      (message) => {
        console.error('Could not fetch campaigns: ' + message);
        setCampaigns(null);
        setLoading(false);
      }
    );
  }, [props.currentOrganization?.id, props.currentProxyPerson, user?.sub]);

  const onChange = useCallback(
    ({ campaignId }: { campaignId: number }) => {
      const querystring = buildQueryString(campaignId);
      history.push(`${location.pathname}${querystring}`);
    },
    [history, location.pathname]
  );

  const onChangeReportItem = useCallback(
    ({ target: { value } }: { target: { value: number } }) => {
      setSelectedReportItem(value);
    },
    []
  );

  const cycleDropdown = useMemo(() => {
    return (
      campaigns?.length &&
      campaigns?.length > 0 && (
        <Row>
          <Col>
            <PerformanceCycleDropdown
              campaignId={campaignId}
              campaigns={campaigns}
              onChange={onChange}
            />
          </Col>
          {allReportItems.length > 0 && (
            <Col>
              <ObjectsDropdown
                selectText={formatMessage({
                  id: 'app.views.engagement.engagement_hub_page.report_selector.dropdown.select_report',
                  defaultMessage: 'Select report...',
                })}
                objects={allReportItems}
                value={selectedReportItem}
                onChange={onChangeReportItem}
              />
            </Col>
          )}
        </Row>
      )
    );
  }, [
    campaignId,
    campaigns,
    onChange,
    onChangeReportItem,
    allReportItems,
    selectedReportItem,
    formatMessage,
  ]);

  const buildTabs = (
    buildQueryString: (campaignId?: number) => string,
    campaignId: number | undefined,
    campaigns: Campaign[] | null,
    cycleDropdown: false | 0 | JSX.Element | undefined,
    noReportsAvailable: boolean,
    formatMessage: IntlShape['formatMessage']
  ) => {
    if (!campaigns?.length) {
      return [];
    }
    const querystring = buildQueryString(campaignId);

    const selectedCampaign =
      campaigns.find((campaign) => campaign.id === campaignId) || campaigns[0]; // use first one until campaignId selected
    return [
      {
        name: formatMessage({
          id: 'app.views.engagement.engagement_hub_page.tab_label.overview',
          defaultMessage: 'Overview',
        }),
        action: cycleDropdown,
        path: consts.ENGAGEMENT_HUB(formatMessage).path + querystring,
        content: noReportsAvailable ? (
          <Card>
            <CardBody>
              <FormattedMessage
                id="app.views.engagement.engagement_hub_page.no_reports.error"
                defaultMessage="There are no reports available for this campaign."
              />
            </CardBody>
          </Card>
        ) : (
          <TakeawaysDashboard
            campaign={selectedCampaign}
            campaigns={campaigns}
            forceShowEngagementSurveyOnly={true}
            setPageStatusIndicator={setPageStatusIndicator}
            campaignReportItemId={selectedReportItem}
          />
        ),
      },
    ];
  };

  const noReportsAvailable =
    allReportItems.length === 0 && reportItemsStatus === 'SUCCESS';

  const tabs = buildTabs(
    buildQueryString,
    campaignId,
    campaigns,
    cycleDropdown,
    noReportsAvailable,
    formatMessage
  );

  if (loading || reportItemsStatus === 'LOADING') {
    return <Loading />;
  } else if (reportItemsStatus === 'ERROR') {
    return (
      <Card>
        <CardBody>
          <FormattedMessage
            id="app.views.engagement.engagement_hub_page.loading_available_reports.error"
            defaultMessage="Error loading the available report. Please try again later."
          />
        </CardBody>
      </Card>
    );
  } else if (tabs?.length > 0) {
    return (
      <Page title={consts.ENGAGEMENT_HUB(formatMessage).name} tabs={tabs} />
    );
  } else {
    return (
      <Page
        title={consts.ENGAGEMENT_HUB(formatMessage).name}
        statusIndicator={pageStatusIndicator}
      >
        <Card>
          <CardBody>
            <EmptyState
              title={formatMessage({
                id: 'app.views.engagement.engagement_hub_page.no_engagement_data.title',
                defaultMessage: 'No engagement data available',
              })}
              subtitle={formatMessage({
                id: 'app.views.engagement.engagement_hub_page.no_engagement_data.subtitle',
                defaultMessage:
                  'No engagement survey data is available to show.',
              })}
            />
          </CardBody>
        </Card>
      </Page>
    );
  }
};

const mapStateToProps = (state: ReduxState) => {
  const { authUser, currentProxyPerson, currentOrganization } = state;

  return {
    authUser,
    currentProxyPerson,
    currentOrganization,
  };
};

export default connect(mapStateToProps)(React.memo(EngagementHubPage));
